import React from "react";

import { translate as t } from "infrastructure/translate/localization";

import { ReactComponent as EducationIcon } from "assets/icons/education-benefit-icon-active.svg";
import { ReactComponent as NurseryIcon } from "assets/icons/nursery-benefit-icon-active.svg";
import { ReactComponent as HealthIcon } from "assets/icons/health-insurance-benefit-icon-active.svg";
import { ReactComponent as RentingIcon } from "assets/icons/renting-benefit-icon-active.svg";
import { ReactComponent as LifeIcon } from "assets/icons/life-insurance-benefit-icon-active.svg";
import { ReactComponent as WellnessIcon } from "assets/icons/wellness-benefit-icon-active.svg";

import { MonetaryAmount } from "app/domain/monetary-amount";
import moment from "moment";

export function getBenefit(key) {
  const benefits = {
    "education-benefit": {
      icon: <EducationIcon />,
      title: t("benefits-section.education-benefit.education"),
    },
    "nursery-benefit": {
      icon: <NurseryIcon />,
      title: t("benefits-section.nursery-benefit.nursery"),
    },
    "health-insurance-benefit": {
      icon: <HealthIcon />,
      title: t("benefits-section.health-insurance-benefit.health-insurance"),
    },
    "renting-benefit": {
      icon: <RentingIcon />,
      title: t("benefits-section.renting-benefit.renting"),
    },
    "life-insurance-benefit": {
      icon: <LifeIcon />,
      title: t("benefits-section.life-insurance-benefit.life-insurance"),
    },
    "wellness-benefit": {
      icon: <WellnessIcon />,
      title: t("benefits-section.ifeel-benefit.ifeel"),
    },
    default: {
      icon: null,
      title: "",
    },
  };

  return benefits[key] || benefits.default;
}

export function formatTimeStamp(timeStamp) {
  if (!timeStamp) return undefined;
  return moment(new Date(timeStamp * 1000)).format("DD/MM/YYYY");
}

export function getBenefitItem({ key, benefit }) {
  const faresTotalAmount = benefit?.fares?.totalAmount?.amountInCents;
  const splitBy = benefit?.benefitDetails?.splitBy;
  const executionHistoryLength = benefit?.executionHistory?.length;
  const numberOfPayments = benefit?.benefitDetails?.numberOfPayments;
  const coverageLastPaymentUntil = benefit?.coverageLastPaymentUntil;

  const formatted = {
    monthlyAmount: MonetaryAmount.of(faresTotalAmount).prettifyMoney({}),
    totalAmountFeesPaid: MonetaryAmount.of(faresTotalAmount)
      .multiply(executionHistoryLength)
      .prettifyMoney({}),
    totalAmountFeesPending: MonetaryAmount.of(faresTotalAmount)
      .multiply(splitBy - executionHistoryLength || 0)
      .prettifyMoney({}),
    coverageLastPaymentUntil: formatTimeStamp(coverageLastPaymentUntil),
  };

  const getTranslate = (label) => {
    if (!label) return "";
    return t(`employees-section.pending-payments-breakdown.${label}`);
  };

  const benefits = {
    "education-benefit": [
      {
        label: getTranslate("monthly-amount"),
        value: formatted.monthlyAmount,
      },
      {
        label: getTranslate("split-by"),
        value: splitBy,
      },
      {
        label: getTranslate("number-monthly-payments-paid"),
        value: executionHistoryLength,
      },
      {
        label: getTranslate("total-amount-paid-monthly-payments"),
        value: formatted.totalAmountFeesPaid,
      },
      {
        label: getTranslate("total-amount-pending-monthly-payments"),
        value: formatted.totalAmountFeesPending,
      },
    ],
    "nursery-benefit": [
      {
        label: getTranslate("amount-paid-covers-current-month"),
        value: formatted.monthlyAmount,
      },
      {
        label: getTranslate("cancellation-effective-date"),
        value: formatted.coverageLastPaymentUntil,
      },
    ],
    "health-insurance-benefit": [
      {
        label: getTranslate("current-months-premium"),
        value: formatted.monthlyAmount,
      },
      {
        label: getTranslate("cancellation-effective-date"),
        value: formatted.coverageLastPaymentUntil,
      },
    ],
    "renting-benefit": [
      {
        label: getTranslate("total-amount-installments"),
        value: formatted.monthlyAmount,
      },
      {
        label: getTranslate("number-total-installments"),
        value: numberOfPayments,
      },
      {
        label: getTranslate("number-paid-installments"),
        value: executionHistoryLength,
      },
      {
        label: getTranslate("total-amount-installments-paid"),
        value: formatted.totalAmountFeesPaid,
      },
      {
        label: getTranslate("total-amount-installments-pending"),
        value: formatted.totalAmountFeesPending,
      },
    ],
    "life-insurance-benefit": [
      {
        label: getTranslate("current-months-premium"),
        value: formatted.monthlyAmount,
      },
      {
        label: getTranslate("cancellation-effective-date"),
        value: formatted.coverageLastPaymentUntil,
      },
    ],
    "wellness-benefit": [
      {
        label: getTranslate("amount-paid-covers-current-month"),
        value: formatted.monthlyAmount,
      },
      {
        label: getTranslate("cancellation-effective-date"),
        value: formatted.coverageLastPaymentUntil,
      },
    ],
  };

  return benefits?.[key] || null;
}
